import type { NetworkRecord } from "@services/networkManagement";
import type { UserFacility } from "@services/facilityManagement";
import { formatNetworkPortfolioSelectorDetail } from "@services/networkDashboardFormat";
import type { MyFacilityListRow } from "types/myFacilityListRow";

export type HoldingCompanyNetworkInput = {
  is_network?: boolean;
  network_id?: number;
};

/** Synthetic facility_id for dropdown/session storage (negative network PK). */
export function networkDropdownId(networkId: number): number {
  return -networkId;
}

export function isHoldingCompanyNetwork(
  facility: HoldingCompanyNetworkInput | null | undefined,
): boolean {
  return Boolean(facility?.is_network && facility.network_id);
}

/** Pill label for grouped rows in facility/network pickers. */
export function facilityGroupBadgeLabel(
  facility: HoldingCompanyNetworkInput | null | undefined,
  isParent: boolean,
): "Network" | "Parent" | null {
  if (isHoldingCompanyNetwork(facility)) return "Network";
  if (isParent) return "Parent";
  return null;
}

export function networkRecordToFacilityRow(network: NetworkRecord): MyFacilityListRow {
  const childCount = network.linked_ccn_count ?? network.child_facility_count ?? 0;
  const dropdownId = networkDropdownId(network.id);
  const networkSubtitle = formatNetworkPortfolioSelectorDetail({
    facilityCount: childCount,
    states: network.facility_states,
    networkId: network.id,
  });
  return {
    id: dropdownId,
    facility_id: dropdownId,
    network_id: network.id,
    is_network: true,
    facility_name: network.name,
    city: network.city ?? "",
    state: network.state ?? "",
    full_address: network.address_1 ?? "",
    facility_type: networkSubtitle || (childCount > 0 ? `Network (${childCount} facilities)` : "Network"),
    facility_type_slug: "network",
    vertical: network.vertical || "healthcare",
    country_code: network.country_code || "USA",
    org_id: 0,
    org_name: network.name,
    child_facilities: [],
    facility_group_id: network.network_group_id,
    is_parent_facility: true,
  };
}

export function networkRecordToUserFacility(network: NetworkRecord): UserFacility {
  const row = networkRecordToFacilityRow(network);
  return {
    id: row.facility_id,
    facility_id: row.facility_id,
    facility_name: row.facility_name,
    org_name: row.org_name,
    city: row.city,
    state: row.state,
    full_address: row.full_address,
    country_code: row.country_code ?? undefined,
    vertical: row.vertical ?? undefined,
    facility_type_slug: row.facility_type_slug,
    facility_group_id: row.facility_group_id,
    is_parent_facility: row.is_parent_facility,
    is_network: true,
    network_id: row.network_id,
    child_facilities: [],
  };
}

